import React, { useState } from 'react';
import { IoClose, IoSave, IoImage } from 'react-icons/io5';
import styles from '../AddProduct.module.css';

function AltTagModal({ isOpen, onClose, image, onSave, imageIndex, productName }) {
    const [altText, setAltText] = useState(image?.altText || '');
    const [isLoading, setIsLoading] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);

    const handleSave = async () => {
        setIsLoading(true);
        try {
            await onSave(imageIndex, altText);
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    const handleAIGenerate = async () => {
        if (!image?.url) return;
        setAiLoading(true);

        const token = localStorage.getItem('token');
        const uid = localStorage.getItem('id');

        try {
            const res = await fetch('https://api.ecomtechbd.com/product/ai-alt-text', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', token, uid },
                body: JSON.stringify({
                    images: [{ url: image.url, index: 0 }],
                    productName: productName || 'Product',
                }),
            });

            if (!res.ok) throw new Error('Server error');

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const data = JSON.parse(line.slice(6));
                        if (data.type === 'complete' && data.success && data.altTexts?.[0]?.altText) {
                            setAltText(data.altTexts[0].altText);
                        }
                    } catch {}
                }
            }
        } catch {
            // silent fail
        } finally {
            setAiLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles['alt-modal-overlay']} onClick={onClose}>
            <div
                className={styles['alt-modal']}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles['alt-modal-header']}>
                    <div className={styles['alt-modal-title']}>
                        <IoImage className={styles['alt-modal-icon']} />
                        <span>Edit Image Alt Text</span>
                    </div>
                    <button
                        onClick={onClose}
                        className={styles['alt-modal-close']}
                        type="button"
                    >
                        <IoClose />
                    </button>
                </div>

                <div className={styles['alt-modal-body']}>
                    <div className={styles['alt-modal-image-preview']}>
                        <img
                            src={image?.url}
                            alt={altText || 'Product image'}
                            className={styles['alt-modal-image']}
                        />
                    </div>

                    <div className={styles['alt-modal-form']}>
                        <label className={styles['alt-modal-label']}>
                            Alt Text (for SEO)
                            <span className={styles['alt-modal-required']}>
                                *
                            </span>
                        </label>
                        <textarea
                            value={altText}
                            onChange={(e) => setAltText(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Describe this image for search engines and screen readers..."
                            className={styles['alt-modal-textarea']}
                            rows={3}
                            maxLength={125}
                            disabled={aiLoading}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className={styles['alt-modal-counter']}>
                                {altText.length}/125 characters
                            </div>
                            <button
                                type="button"
                                onClick={handleAIGenerate}
                                disabled={aiLoading || !image?.url}
                                style={{
                                    padding: '4px 12px',
                                    background: aiLoading ? '#e8e8ee' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                    color: aiLoading ? '#6d7175' : '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    cursor: aiLoading ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {aiLoading ? 'Generating...' : 'AI Generate'}
                            </button>
                        </div>
                        <div className={styles['alt-modal-tips']}>
                            <strong>SEO Tips:</strong>
                            <ul>
                                <li>Be descriptive and specific</li>
                                <li>Include relevant keywords</li>
                                <li>Keep it under 125 characters</li>
                                <li>Describe what's in the image</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className={styles['alt-modal-footer']}>
                    <button
                        onClick={onClose}
                        className={styles['alt-modal-cancel']}
                        type="button"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!altText.trim() || isLoading}
                        className={styles['alt-modal-save']}
                        type="button"
                    >
                        {isLoading ? (
                            <>
                                <div
                                    className={styles['alt-modal-spinner']}
                                ></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <IoSave />
                                Save Alt Text
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AltTagModal;
